/**
 * Application root: providers, error boundary, router.
 *
 * The landing page sits outside the providers deliberately. `DataProvider`
 * prefetches categories, doctors and packages for every visitor — that is right
 * for the app, where those are needed within a click or two, but the landing
 * page is a public marketing page that uses none of them. Mounting it inside
 * would cost three API round-trips on the highest-traffic page in the product,
 * and log console errors there whenever the API is having a bad day.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import { NotificationProvider } from './context/NotificationContext';
import { PermissionProvider } from './context/PermissionContext';
import { ThemeProvider } from './context/ThemeContext';
import { I18nProvider } from './i18n/I18nProvider';
import { LocaleSync } from './i18n/LocaleSync';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { RouteFallback } from './app/guards';
import { AppRoutes } from './app/router';

/**
 * The two provider-free pages are split out of the main bundle.
 *
 * The patient's visit page is the reason. It is opened from a WhatsApp message,
 * on a phone, on whatever signal the person has outside a clinic, by somebody
 * who will read it once — and without this it would arrive bundled with the
 * entire signed-in application, none of which it uses. The landing page is
 * split for the same reason and has the highest traffic in the product.
 *
 * `RouteFallback` was written in guards.jsx for suspended route chunks and had
 * no caller until now.
 */
const LandingPage = lazy(() =>
  import('./features/landing/LandingPage').then((m) => ({
    default: m.LandingPage,
  })),
);
const PublicVisitPage = lazy(() =>
  import('./features/public/PublicVisitPage').then((m) => ({
    default: m.PublicVisitPage,
  })),
);
/** Dashboard 14 — the patient's whole record, from the link or QR the hospital handed over. */
const PatientPortalPage = lazy(() =>
  import('./features/public/PatientPortalPage').then((m) => ({
    default: m.PatientPortalPage,
  })),
);

/** QA report §5.3 — the waiting-hall screen and the page behind the ticket's QR. */
const QueueDisplayPage = lazy(() =>
  import('./features/public/QueueDisplayPage').then((m) => ({
    default: m.QueueDisplayPage,
  })),
);
// المرحلة 1.35: a voucher's soft copy, opened from WhatsApp.
const PublicReceiptPage = lazy(() =>
  import('./features/public/PublicReceiptPage').then((m) => ({
    default: m.PublicReceiptPage,
  })),
);
const QueueTicketPage = lazy(() =>
  import('./features/public/QueueTicketPage').then((m) => ({
    default: m.QueueTicketPage,
  })),
);

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public, provider-free */}
            <Route path="/landingpage" element={<LandingPage />} />

            {/*
            The patient's visit page, opened from a WhatsApp link with no
            account and no session. Outside the providers for the same reason
            the landing page is, and one more: AuthProvider would read a stale
            `capsule_session` from a shared clinic tablet and render the shell
            around somebody else's appointment. This page must know nothing
            about who is signed in.
          */}
            <Route path="/v/:token" element={<PublicVisitPage />} />
            {/* The patient portal: the same rules, and the same reasons. */}
            <Route path="/p/:token" element={<PatientPortalPage />} />
            {/* The live queue: a TV with no account, a patient with a paper slip. */}
            <Route path="/display/:token" element={<QueueDisplayPage />} />
            <Route path="/q/:token" element={<QueueTicketPage />} />
            {/* A receipt's soft copy (المرحلة 1.35): the same rules again. */}
            <Route path="/r/:token" element={<PublicReceiptPage />} />

            {/* Everything else runs inside the app's data and auth layers */}
            <Route
              path="*"
              element={
                // The two languages and the country (المرحلة 1.1) wrap the app
                // and its sign-in screens; the public pages above stay Arabic.
                <I18nProvider>
                  <AuthProvider>
                    <PermissionProvider>
                      <LocaleSync />
                      <ThemeProvider>
                        <NotificationProvider>
                          <DataProvider>
                            <AppRoutes />
                          </DataProvider>
                        </NotificationProvider>
                      </ThemeProvider>
                    </PermissionProvider>
                  </AuthProvider>
                </I18nProvider>
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
